import type { Entitlement, Vod } from '../api/types';
import {
  MIN_REEL_VOD_SEC,
  STUDIO_COPY,
  formatAllotmentCopy,
  formatCreditBalanceCopy,
  formatFreeAllotmentCopy,
} from '../config';
import { formatResetDate } from './format';

/** True when the backend has not marked this VOD as an unsupported game. */
export function isGameEligibleVod(
  vod: Pick<Vod, 'rtts_vod_eligible'>
): boolean {
  return vod.rtts_vod_eligible !== false;
}

/** True when the VOD is long enough to generate a reel. */
export function isReelEligibleVod(
  vod: Pick<Vod, 'duration_sec'>,
  minSec: number = MIN_REEL_VOD_SEC
): boolean {
  return Number(vod.duration_sec) >= minSec;
}

/** Admin / comped / unlimited — no counter, no upgrade CTA. */
export function isUnlimitedEntitlement(ent: Entitlement): boolean {
  return ent.tier === 'admin' || ent.tier === 'comped' || ent.unlimited === true;
}

export type VodsEntitlementIndicator = {
  text: string;
  showUpgrade: boolean;
};

/**
 * Compact entitlement line for the VOD picker / dashboard.
 * Unknown future tiers: no upgrade CTA, don't block.
 */
export function formatVodsEntitlementIndicator(
  ent: Entitlement
): VodsEntitlementIndicator {
  if (isUnlimitedEntitlement(ent)) {
    return { text: STUDIO_COPY.unlimitedBadge, showUpgrade: false };
  }

  if (ent.tier === 'paid') {
    const remaining = ent.period_reels_remaining;
    const cap = ent.period_cap;
    if (typeof remaining === 'number' && typeof cap === 'number') {
      const reset = ent.period_resets_at
        ? formatResetDate(ent.period_resets_at)
        : 'next month';
      return {
        text: formatAllotmentCopy(remaining, cap, reset),
        showUpgrade: false,
      };
    }
    return { text: STUDIO_COPY.unlimitedBadge, showUpgrade: false };
  }

  if (ent.tier === 'free') {
    const credits = ent.reel_credits ?? 0;
    const remaining = ent.reels_remaining;
    const cap = ent.free_cap;
    if (credits > 0) {
      return {
        text: formatCreditBalanceCopy(credits),
        showUpgrade: false,
      };
    }
    if (typeof remaining === 'number' && remaining <= 0) {
      return {
        text: STUDIO_COPY.freeCapReached,
        showUpgrade: true,
      };
    }
    const n = typeof remaining === 'number' ? remaining : cap;
    return {
      text: formatFreeAllotmentCopy(
        typeof n === 'number' ? n : 0,
        typeof cap === 'number' ? cap : 1
      ),
      showUpgrade: false,
    };
  }

  // Unrecognized tier — forward-compatible: no CTA, don't block.
  return { text: STUDIO_COPY.unlimitedBadge, showUpgrade: false };
}
